/**
 * Conditions d'achat — liste des fournisseurs à gauche, leurs conditions à droite (registre B26, lot P1).
 *
 * Pour chaque fournisseur ou marque : la remise (ou les remises en cascade, ou le prix net) et le coefficient qui
 * reconstitue le prix de vente, posés « sur tout le fournisseur », sur chaque FAMILLE de son tarif ou sur UNE
 * référence. La plus précise l'emporte : référence > famille > tout le fournisseur. Chaque condition est DATÉE :
 * en changer ajoute une ligne, l'ancienne reste lisible ; une faute de frappe s'archive (« elle n'a jamais existé »).
 *
 * AUCUNE valeur n'est proposée d'avance : la marge est la décision de la direction. Une famille sans condition est
 * montrée « aucune condition », en orange, jamais complétée. Le bloc « Essayer une référence » montre chaque étape
 * du calcul sur la règle enregistrée : c'est ainsi qu'on la vérifie avant qu'elle serve à quoi que ce soit.
 *
 * MISE EN PAGE : celle des écrans « Gammes » et « Tarifs » (liste compacte à gauche avec recherche en tête, lecture à
 * droite, chaque colonne défile seule, les messages du dernier geste restent collés en haut de la colonne de droite).
 *
 * DROITS (affichage seulement : l'API décide) : tout l'écran est financier. Sans `pricing:read` il n'affiche rien
 * et ne demande rien à l'API ; définir ou archiver une condition exige `pricing:manage` ET `pricing:read`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  ApiError,
  api,
  type ConditionAchat,
  type ConditionsDuTiers,
  type FamilleDuTarif,
  type Party,
  type ResultatEssaiPrix,
  type TarifResume,
} from '../api/client';
import { useDroitsGammes } from '../auth/useDroits';
import { ChampReference } from '../composants/ChampReference';
import { FormulaireCondition, type CibleCondition } from '../composants/FormulaireCondition';
import { ErreurFormulaire } from '../composants/Formulaire';
import { RecalculPrixGammes } from '../composants/RecalculPrixGammes';
import {
  ROLES_FOURNISSEUR,
  candidatsAjout,
  construireLignes,
  formaterCoefficient,
  fournisseursListes,
  libelleAchat,
  margeDuCoefficient,
  phraseRaison,
  plierTexte,
  tiersMasques,
  type FournisseurAvecConditions,
  type LigneConditions,
} from '../domaine/conditions-achat';
import { dateFrTarif, eurosFr, nombreFr } from '../domaine/tarifs';

const norme = plierTexte;

const NIVEAU: Record<'ARTICLE' | 'FAMILLE' | 'FOURNISSEUR', string> = {
  ARTICLE: 'exception pour cette référence',
  FAMILLE: 'condition de sa famille',
  FOURNISSEUR: 'condition « tout le fournisseur »',
};

/** Les familles de plusieurs tarifs du même fournisseur, fusionnées par clé (les références s'additionnent). */
function fusionnerFamilles(listes: readonly (readonly FamilleDuTarif[])[]): FamilleDuTarif[] {
  const parCle = new Map<string, { cle: string; famille: string; graphies: Set<string>; references: number }>();
  for (const liste of listes) {
    for (const f of liste) {
      const existante = parCle.get(f.cle);
      if (existante) {
        existante.references += f.references;
        f.graphies.forEach((g) => existante.graphies.add(g));
      } else {
        parCle.set(f.cle, { cle: f.cle, famille: f.famille, graphies: new Set(f.graphies), references: f.references });
      }
    }
  }
  return [...parCle.values()]
    .map((f) => ({ cle: f.cle, famille: f.famille, graphies: [...f.graphies], references: f.references }))
    .sort((a, b) => b.references - a.references || a.famille.localeCompare(b.famille, 'fr'));
}

export function ConditionsAchatPage() {
  const [parametres, setParametres] = useSearchParams();
  const selectionne = parametres.get('fournisseur');
  const droits = useDroitsGammes();
  const peutVoir = droits.peutVoirPrix;
  const peutEcrire = droits.peutEcrirePrix;

  const [recherche, setRecherche] = useState(parametres.get('q') ?? '');
  // TOUS les tiers marque, fabricant ou fournisseur : ce qu'on peut ajouter. Ce n'est PAS la liste affichée.
  const [fournisseurs, setFournisseurs] = useState<Party[] | null>(null);
  const [tarifs, setTarifs] = useState<TarifResume[]>([]);
  const [comptes, setComptes] = useState<FournisseurAvecConditions[] | null>(null);
  // Les fournisseurs ajoutés pendant cette visite : listés tant qu'ils n'ont ni tarif ni condition, puis ils restent
  // d'eux-mêmes (la condition qu'on leur pose les fait apparaître).
  const [ajoutes, setAjoutes] = useState<ReadonlySet<string>>(new Set());
  const [ouvertAjout, setOuvertAjout] = useState(false);
  const [rechercheAjout, setRechercheAjout] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  const [conditions, setConditions] = useState<ConditionsDuTiers | null>(null);
  const [familles, setFamilles] = useState<FamilleDuTarif[]>([]);
  const [afficherArchivees, setAfficherArchivees] = useState(false);
  const [rechargement, setRechargement] = useState(0);

  const [panneau, setPanneau] = useState<{ cible: CibleCondition; actuelle: ConditionAchat | null } | null>(null);
  const [deplie, setDeplie] = useState<ReadonlySet<string>>(new Set());
  const [erreurAction, setErreurAction] = useState<ApiError | Error | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [avertissements, setAvertissements] = useState<readonly string[]>([]);
  const [enCours, setEnCours] = useState(false);

  const majUrl = useCallback(
    (fournisseur: string | null, q: string) => {
      const p = new URLSearchParams();
      if (q.trim()) p.set('q', q);
      if (fournisseur) p.set('fournisseur', fournisseur);
      setParametres(p, { replace: true });
    },
    [setParametres],
  );

  // La liste des fournisseurs : les tiers marque, fournisseur ou fabricant, une seule fois chacun.
  useEffect(() => {
    if (!peutVoir) return;
    let annule = false;
    Promise.all([...ROLES_FOURNISSEUR.map((role) => api.parties(undefined, role)), api.tarifs()])
      .then((resultats) => {
        if (annule) return;
        const listeTarifs = resultats[ROLES_FOURNISSEUR.length] as TarifResume[];
        const parId = new Map<string, Party>();
        for (const liste of resultats.slice(0, ROLES_FOURNISSEUR.length) as Party[][]) {
          for (const p of liste) if (p.status !== 'ARCHIVED') parId.set(p.id, p);
        }
        setFournisseurs([...parId.values()].sort((a, b) => a.legalName.localeCompare(b.legalName, 'fr')));
        setTarifs(listeTarifs);
        setErreur(null);
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Chargement des fournisseurs impossible.');
      });
    return () => {
      annule = true;
    };
  }, [peutVoir]);

  // Les fournisseurs avec une condition en cours : relus à chaque condition posée ou archivée (`rechargement`), pour
  // qu'un fournisseur apparaisse dès qu'on lui pose sa première condition, et disparaisse quand il n'en a plus.
  useEffect(() => {
    if (!peutVoir) return;
    let annule = false;
    api
      .fournisseursAvecConditions()
      .then((rendus) => {
        if (!annule) setComptes(rendus);
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Chargement des fournisseurs impossible.');
      });
    return () => {
      annule = true;
    };
  }, [peutVoir, rechargement]);

  // La liste affichée : ceux avec qui l'on travaille (un tarif, une condition, ou qu'on vient d'ajouter) — pas toutes
  // les marques du parc : avoir une machine d'une marque ne veut pas dire qu'on achète chez elle.
  const listes = useMemo(
    () => (fournisseurs && comptes ? fournisseursListes(fournisseurs, tarifs, comptes, ajoutes, selectionne) : null),
    [fournisseurs, comptes, tarifs, ajoutes, selectionne],
  );
  const masques = useMemo(() => (fournisseurs && listes ? tiersMasques(fournisseurs, listes) : []), [fournisseurs, listes]);
  const candidats = useMemo(() => candidatsAjout(masques, rechercheAjout), [masques, rechercheAjout]);

  const filtres = useMemo(
    () => (listes ?? []).filter((f) => norme(f.legalName).includes(norme(recherche.trim()))),
    [listes, recherche],
  );

  // Rien n'est choisi : on montre le premier fournisseur plutôt qu'une colonne vide.
  useEffect(() => {
    const premier = filtres[0];
    if (peutVoir && listes !== null && !selectionne && premier) majUrl(premier.id, recherche);
  }, [peutVoir, listes, filtres, selectionne, recherche, majUrl]);

  const tarifsDuFournisseur = useMemo(
    () => tarifs.filter((t) => t.partyId === selectionne && !t.archivedAt),
    [tarifs, selectionne],
  );

  // Les conditions du fournisseur choisi, et les familles de ses tarifs en vigueur.
  useEffect(() => {
    setConditions(null);
    setFamilles([]);
    setDeplie(new Set());
    if (!peutVoir || !selectionne) return;
    let annule = false;
    const aLire = tarifsDuFournisseur.filter((t) => t.versionEnVigueur !== null);
    Promise.all([api.conditionsAchat(selectionne, afficherArchivees), Promise.all(aLire.map((t) => api.famillesTarif(t.id)))])
      .then(([c, f]) => {
        if (annule) return;
        setConditions(c);
        setFamilles(fusionnerFamilles(f.map((x) => x.familles)));
      })
      .catch((e: unknown) => {
        if (!annule) setErreur(e instanceof ApiError ? e.message : 'Chargement des conditions impossible.');
      });
    return () => {
      annule = true;
    };
  }, [peutVoir, selectionne, afficherArchivees, rechargement, tarifsDuFournisseur]);

  const lignes = useMemo(() => (conditions ? construireLignes(conditions.items, familles) : []), [conditions, familles]);

  const selectionner = (id: string) => {
    setErreurAction(null);
    setSucces(null);
    setAvertissements([]);
    majUrl(id, recherche);
  };

  const changerRecherche = (saisie: string) => {
    setRecherche(saisie);
    majUrl(selectionne, saisie);
  };

  // « Ajouter un fournisseur » : un tiers masqué (aucun tarif, aucune condition) entre dans la liste et se sélectionne.
  const ajouter = (id: string) => {
    setAjoutes((courant) => new Set(courant).add(id));
    setOuvertAjout(false);
    setRechercheAjout('');
    setRecherche('');
    setErreurAction(null);
    setSucces(null);
    setAvertissements([]);
    majUrl(id, '');
  };

  const basculer = (cle: string) =>
    setDeplie((courant) => {
      const suivant = new Set(courant);
      if (suivant.has(cle)) suivant.delete(cle);
      else suivant.add(cle);
      return suivant;
    });

  const archiver = async (c: ConditionAchat) => {
    const confirme = window.confirm(
      `Archiver cette condition (${libelleAchat(c)}, coefficient ${formaterCoefficient(c.coefficient)}, du ${dateFrTarif(c.validFrom)}) ?\n\n` +
        'Elle sera écartée comme si elle n’avait jamais existé : la condition précédente, s’il y en a une, redevient celle qui s’applique. ' +
        'À utiliser pour corriger une faute de saisie. Pour CHANGER de condition, définis-en une nouvelle. C’est définitif.',
    );
    if (!confirme) return;
    setEnCours(true);
    setErreurAction(null);
    setSucces(null);
    setAvertissements([]);
    try {
      await api.archiverConditionAchat(c.id);
      setSucces('Condition archivée.');
      setRechargement((n) => n + 1);
    } catch (e) {
      setErreurAction(e instanceof ApiError || e instanceof Error ? e : new Error('Archivage impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  const fournisseur = (fournisseurs ?? []).find((f) => f.id === selectionne) ?? null;

  if (!peutVoir) {
    return (
      <div className="gammes-page">
        <div className="entete-page">
          <div>
            <h1>Conditions d’achat</h1>
            <p>Les remises, les prix nets et les coefficients de marge de chaque fournisseur.</p>
          </div>
        </div>
        <div className="message info">
          Cet écran est réservé aux personnes qui voient les prix : les remises et les coefficients sont des
          informations financières.
        </div>
      </div>
    );
  }

  return (
    <div className="gammes-page">
      <div className="entete-page">
        <div>
          <h1>Conditions d’achat</h1>
          <p>
            Pour chaque fournisseur : ta remise (ou tes remises en cascade, ou le prix net) et le coefficient qui
            reconstitue le prix de vente. La condition la plus précise l’emporte : une référence, puis sa famille, puis
            tout le fournisseur. Aucune valeur n’est jamais choisie à ta place.
          </p>
        </div>
      </div>

      {!peutEcrire && (
        <div className="message info">
          Vous consultez les conditions en lecture seule : en définir ou en archiver demande le droit de gérer les prix.
        </div>
      )}

      <div className="gammes-mise-en-page">
        <div className="carte gammes-liste">
          <div className="gammes-liste-entete">
            <input
              type="text"
              autoFocus
              aria-label="Rechercher un fournisseur"
              placeholder="Rechercher un fournisseur…"
              value={recherche}
              onChange={(e) => changerRecherche(e.target.value)}
            />
            <div className="gammes-liste-outils">
              <span className="aide">
                {listes === null
                  ? 'Chargement…'
                  : filtres.length === listes.length
                    ? `${listes.length} fournisseur${listes.length > 1 ? 's' : ''}`
                    : `${filtres.length} sur ${listes.length}`}
              </span>
              {peutEcrire && listes !== null && masques.length > 0 && (
                <button
                  type="button"
                  className="secondaire"
                  aria-expanded={ouvertAjout}
                  onClick={() => setOuvertAjout((o) => !o)}
                >
                  {ouvertAjout ? 'Fermer' : '+ Ajouter un fournisseur'}
                </button>
              )}
            </div>
          </div>

          {ouvertAjout && (
            <div className="ajout-fournisseur">
              <input
                type="text"
                autoFocus
                aria-label="Chercher un tiers à ajouter"
                placeholder="Marque, fabricant ou fournisseur à ajouter…"
                value={rechercheAjout}
                onChange={(e) => setRechercheAjout(e.target.value)}
              />
              <p className="aide">
                Seuls les fournisseurs qui ont un tarif ou une condition sont listés. Celui que tu ajoutes y reste dès que
                tu lui poses une condition.
              </p>
              {candidats.items.length === 0 ? (
                <div className="vide">Aucun autre tiers ne correspond à « {rechercheAjout.trim()} ».</div>
              ) : (
                <ul className="parc-lignes">
                  {candidats.items.map((t) => (
                    <li key={t.id}>
                      <button type="button" className="parc-ligne" onClick={() => ajouter(t.id)}>
                        <div className="parc-ligne-titre">
                          <strong>{t.legalName}</strong>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {candidats.total > candidats.items.length && (
                <p className="aide">
                  {candidats.items.length} sur {candidats.total} : continue à taper pour préciser.
                </p>
              )}
            </div>
          )}

          <div className="gammes-liste-corps">
            {erreur && <div className="message erreur">{erreur}</div>}
            {listes === null ? (
              !erreur && <div className="vide">Chargement…</div>
            ) : listes.length === 0 ? (
              <div className="vide">
                Aucun fournisseur n’a encore de tarif ni de condition d’achat. Importe un tarif (écran « Tarifs »)
                {masques.length > 0 ? ', ou ajoute un fournisseur.' : '.'}
              </div>
            ) : filtres.length === 0 ? (
              <div className="vide">Aucun fournisseur ne correspond à « {recherche.trim()} ».</div>
            ) : (
              <ul className="parc-lignes">
                {filtres.map((f) => {
                  const nbTarifs = tarifs.filter((t) => t.partyId === f.id && !t.archivedAt).length;
                  return (
                    <li key={f.id}>
                      <button
                        type="button"
                        className={`parc-ligne${f.id === selectionne ? ' actif' : ''}`}
                        aria-pressed={f.id === selectionne}
                        onClick={() => selectionner(f.id)}
                      >
                        <div className="parc-ligne-titre">
                          <strong>{f.legalName}</strong>
                        </div>
                        <div className="aide">
                          {nbTarifs > 0 ? `${nbTarifs} tarif${nbTarifs > 1 ? 's' : ''}` : 'aucun tarif importé'}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="gammes-fiche">
          {(erreurAction || succes) && (
            <div className="gammes-fiche-entete">
              <ErreurFormulaire
                message={erreurAction?.message ?? null}
                details={erreurAction instanceof ApiError ? erreurAction.details : undefined}
              />
              {succes && <div className="message succes">{succes}</div>}
              {avertissements.map((a) => (
                <div key={a} className="message avertissement">
                  {a}
                </div>
              ))}
            </div>
          )}

          {!selectionne && (
            <div className="carte">
              <div className="vide">Choisissez un fournisseur dans la liste pour lire et définir ses conditions d’achat.</div>
            </div>
          )}

          {selectionne && fournisseur && (
            <>
              <div className="carte">
                <div className="carte-corps">
                  <h2>{fournisseur.legalName}</h2>
                  {tarifsDuFournisseur.length === 0 ? (
                    <p className="aide">
                      Ce fournisseur n’a aucun tarif importé : il n’a pas de familles. Tu peux poser une condition sur
                      « tout le fournisseur », ou par référence. Il reste dans la liste tant qu’il a un tarif ou une
                      condition.
                    </p>
                  ) : (
                    <p className="aide">
                      Tarifs : {tarifsDuFournisseur.map((t) => t.label).join(', ')}.
                      {familles.length > 0
                        ? ` ${nombreFr(familles.length)} famille${familles.length > 1 ? 's' : ''} dans la version en vigueur.`
                        : ' Aucune version en vigueur : pas de familles à montrer.'}
                    </p>
                  )}
                </div>
              </div>

              <BlocEssai partyId={fournisseur.id} />

              {/* Les prix des gammes se figent à la publication : recalculer toute la marque d'un coup. Il faut gérer les gammes ET les prix. */}
              {droits.peutGerer && peutEcrire && (
                <RecalculPrixGammes partyId={fournisseur.id} partyName={fournisseur.legalName} droits={droits} />
              )}

              <div className="carte">
                <div className="carte-corps">
                  <div className="gammes-liste-outils">
                    <h3 style={{ margin: 0 }}>Conditions</h3>
                    <label className="case">
                      <input
                        type="checkbox"
                        checked={afficherArchivees}
                        onChange={(e) => setAfficherArchivees(e.target.checked)}
                      />
                      Afficher les conditions archivées
                    </label>
                  </div>

                  {conditions === null ? (
                    <div className="vide">Chargement des conditions…</div>
                  ) : (
                    <div className="tableau-defilant">
                      <table className="conditions-table">
                        <thead>
                          <tr>
                            <th>Portée</th>
                            <th>Achat</th>
                            <th>Coefficient</th>
                            <th>Depuis le</th>
                            <th>À venir</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {lignes.map((l) => (
                            <LigneDuTableau
                              key={l.cle}
                              ligne={l}
                              ouverte={deplie.has(l.cle)}
                              peutEcrire={peutEcrire}
                              occupe={enCours}
                              onBasculer={() => basculer(l.cle)}
                              onDefinir={() =>
                                setPanneau({
                                  cible: { portee: l.portee, famille: l.portee === 'FAMILLE' ? l.titre : null, reference: l.portee === 'REFERENCE' ? l.titre : null },
                                  actuelle: l.enVigueur,
                                })
                              }
                              onArchiver={(c) => void archiver(c)}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {peutEcrire && conditions !== null && (
                    <div className="saisie-ligne">
                      <button
                        className="secondaire"
                        onClick={() => setPanneau({ cible: { portee: 'REFERENCE', famille: null, reference: null }, actuelle: null })}
                      >
                        Ajouter une exception pour une référence
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {panneau && fournisseur && (
        <FormulaireCondition
          partyId={fournisseur.id}
          partyName={fournisseur.legalName}
          cible={panneau.cible}
          actuelle={panneau.actuelle}
          onFermer={() => setPanneau(null)}
          onEnregistree={(r) => {
            setPanneau(null);
            setErreurAction(null);
            setSucces('Condition enregistrée : elle s’applique à partir de sa date d’effet.');
            setAvertissements(r.avertissements);
            setRechargement((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------- lignes

function LigneDuTableau({
  ligne,
  ouverte,
  peutEcrire,
  occupe,
  onBasculer,
  onDefinir,
  onArchiver,
}: {
  ligne: LigneConditions;
  ouverte: boolean;
  peutEcrire: boolean;
  occupe: boolean;
  onBasculer: () => void;
  onDefinir: () => void;
  onArchiver: (c: ConditionAchat) => void;
}) {
  const c = ligne.enVigueur;
  const marge = c ? margeDuCoefficient(c.coefficient) : null;
  const avenir = ligne.aVenir;
  return (
    <>
      <tr>
        <td>
          <strong>{ligne.titre}</strong>
          {ligne.references !== null && <div className="aide">{nombreFr(ligne.references)} références</div>}
          {ligne.graphies.length > 0 && (
            <div className="aide" style={{ color: 'var(--orange)' }}>
              Plusieurs graphies dans le tarif : {ligne.graphies.join(' · ')}
            </div>
          )}
          {ligne.sansCorrespondance && (
            <div className="aide" style={{ color: 'var(--orange)' }}>
              Aucune famille de ce nom dans le tarif en vigueur.
            </div>
          )}
          {ligne.portee === 'REFERENCE' && <div className="aide">Exception pour une référence</div>}
        </td>
        <td>
          {c ? (
            libelleAchat(c)
          ) : ligne.portee === 'FAMILLE' ? (
            <span className="etat attente">Aucune condition</span>
          ) : (
            <span className="aide">—</span>
          )}
        </td>
        <td>
          {c ? (
            <>
              {formaterCoefficient(c.coefficient)}
              {marge && <div className="aide">marge {marge}</div>}
            </>
          ) : (
            <span className="aide">—</span>
          )}
        </td>
        <td>{c ? dateFrTarif(c.validFrom) : <span className="aide">—</span>}</td>
        <td>
          {avenir ? (
            <>
              <span className="etat attente">à venir</span>
              <div className="aide">
                {libelleAchat(avenir)}, coef. {formaterCoefficient(avenir.coefficient)}, le {dateFrTarif(avenir.validFrom)}
              </div>
            </>
          ) : (
            <span className="aide">—</span>
          )}
        </td>
        <td className="conditions-actions">
          {peutEcrire && (
            <button className="secondaire" disabled={occupe} onClick={onDefinir}>
              {c ? 'Nouvelle condition' : 'Définir'}
            </button>
          )}
          {ligne.historique.length > 0 && (
            <button className="secondaire discret" onClick={onBasculer} aria-expanded={ouverte}>
              Historique ({ligne.historique.length}) {ouverte ? '▴' : '▾'}
            </button>
          )}
        </td>
      </tr>
      {ouverte && (
        <tr className="conditions-historique">
          <td colSpan={6}>
            <ul>
              {ligne.historique.map((h) => (
                <li key={h.id} className={h.archivedAt ? 'archivee' : ''}>
                  <span>
                    <strong>{dateFrTarif(h.validFrom)}</strong> — {libelleAchat(h)}, coefficient {formaterCoefficient(h.coefficient)}
                    {h.note && <em> — {h.note}</em>}
                  </span>
                  {h.archivedAt && <span className="etat revoque">Archivée</span>}
                  {h.enVigueur && <span className="etat actif">En vigueur</span>}
                  {h.aVenir && !h.archivedAt && <span className="etat attente">À venir</span>}
                  {peutEcrire && !h.archivedAt && (
                    <button className="secondaire danger-discret" disabled={occupe} onClick={() => onArchiver(h)}>
                      Archiver (faute de saisie)
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

// ------------------------------------------------------------------------------------------ l'essai

/**
 * « Essayer une référence » : il tape une référence (et, s'il le veut, un prix du tarif ou une date), et voit chaque
 * étape du calcul sur la règle ENREGISTRÉE — ou la raison pour laquelle il n'y a pas de prix. N'écrit rien.
 */
function BlocEssai({ partyId }: { partyId: string }) {
  const [reference, setReference] = useState('');
  const [prix, setPrix] = useState('');
  const [date, setDate] = useState('');
  const [resultat, setResultat] = useState<ResultatEssaiPrix | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  // Changer de fournisseur efface l'essai précédent : il ne parlerait plus du bon fournisseur.
  useEffect(() => {
    setResultat(null);
    setErreur(null);
  }, [partyId]);

  const essayer = async () => {
    if (reference.trim() === '') {
      setErreur('Indique une référence.');
      return;
    }
    const prixTexte = prix.trim().replace(',', '.');
    if (prixTexte !== '' && !/^\d+(\.\d{1,2})?$/.test(prixTexte)) {
      setErreur('Le prix du tarif est un montant, deux décimales au plus.');
      return;
    }
    setEnCours(true);
    setErreur(null);
    try {
      setResultat(
        await api.essaiPrix({
          partyId,
          reference: reference.trim(),
          ...(date ? { date } : {}),
          ...(prixTexte ? { prix: prixTexte } : {}),
        }),
      );
    } catch (e) {
      setResultat(null);
      setErreur(e instanceof ApiError ? e.message : 'Essai impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="carte">
      <div className="carte-corps">
        <h3 style={{ marginTop: 0 }}>Essayer une référence</h3>
        <p className="aide">
          Vérifie ta règle sur une référence : le prix du tarif, la famille, la condition retenue, puis le prix d’achat et
          le prix de vente. Rien n’est écrit.
        </p>
        <form
          className="saisie-ligne"
          onSubmit={(e) => {
            e.preventDefault();
            void essayer();
          }}
        >
          {/* Règle de Benjamin : tout champ « référence » propose la liste par le DÉBUT de la référence. */}
          <ChampReference
            fournisseurId={partyId}
            libelle="Référence à essayer"
            valeur={reference}
            placeholder="Référence (le début suffit)"
            desactive={false}
            onChange={setReference}
            onChoisir={(choix) => setReference(choix.reference)}
          />
          <input
            type="text"
            inputMode="decimal"
            aria-label="Prix du tarif (facultatif)"
            placeholder="Prix du tarif (si absente du tarif)"
            value={prix}
            onChange={(e) => setPrix(e.target.value)}
          />
          <input type="date" aria-label="À la date du (facultatif)" title="À la date du (aujourd’hui si vide)" value={date} onChange={(e) => setDate(e.target.value)} />
          <button type="submit" disabled={enCours}>
            {enCours ? 'Calcul…' : 'Essayer'}
          </button>
        </form>

        {erreur && <div className="message erreur">{erreur}</div>}

        {resultat && (
          <div className="essai-resultat">
            {resultat.tarif ? (
              <p>
                Tarif « {resultat.tarif.tarif} » (version {resultat.tarif.versionNumber}) : <strong>{resultat.tarif.reference}</strong>{' '}
                — {resultat.tarif.description}. Famille : <strong>{resultat.tarif.famille ?? 'inconnue'}</strong>. Prix du tarif :{' '}
                <strong>{eurosFr(resultat.tarif.prix)}</strong>.
              </p>
            ) : resultat.ambigue ? (
              <p className="aide">Cette référence est présente plusieurs fois dans les tarifs, avec des familles ou des prix différents.</p>
            ) : (
              <p className="aide">Cette référence n’est dans aucun tarif en vigueur de ce fournisseur{prix.trim() ? ' : le prix saisi sert de prix du tarif.' : '.'}</p>
            )}

            {resultat.condition && (
              <p>
                Condition retenue ({NIVEAU[resultat.condition.niveau]}, depuis le {dateFrTarif(resultat.condition.validFrom)}) :{' '}
                <strong>{libelleAchat(resultat.condition)}</strong>, coefficient{' '}
                <strong>{formaterCoefficient(resultat.condition.coefficient)}</strong>.
              </p>
            )}

            {resultat.prix ? (
              <table className="essai-prix">
                <tbody>
                  <tr>
                    <td>Prix du tarif</td>
                    <td>{eurosFr(resultat.prix.tarif)}</td>
                  </tr>
                  <tr>
                    <td>Prix d’achat</td>
                    <td>{eurosFr(resultat.prix.achat)}</td>
                  </tr>
                  <tr className="essai-vente">
                    <td>Prix de vente</td>
                    <td>{eurosFr(resultat.prix.vente)}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              resultat.raison && <div className="message avertissement">{phraseRaison(resultat.raison)}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
